import { evaluatePassword } from "@/features/auth/passwordStrength";
import { cn } from "@/lib/utils";

type PasswordStrengthMeterProps = {
    password: string;
    /** Optional id used by aria-describedby on the password input */
    id?: string;
    className?: string;
};

/**
 * 4-segment strength meter. Empty state shows all segments in the neutral
 * border color. As strength increases, segments fill left-to-right with the
 * appropriate brand color.
 */
export function PasswordStrengthMeter({
    password,
    id,
    className,
}: PasswordStrengthMeterProps) {
    const strength = evaluatePassword(password);
    const segments = 4;
    const filled = Math.min(strength.score, segments);

    return (
        <div
            id={id}
            data-testid="password-strength"
            className={cn("mt-2", className)}
            aria-live="polite"
        >
            <div className="flex items-center gap-1.5">
                {Array.from({ length: segments }).map((_, i) => (
                    <span
                        key={i}
                        className={cn(
                            "h-1.5 flex-1 rounded-full transition-colors duration-300",
                            i < filled ? strength.color : "bg-border",
                        )}
                    />
                ))}
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[11px]">
                <span
                    className={cn(
                        "font-semibold",
                        strength.textColor,
                    )}
                    data-testid="password-strength-label"
                >
                    {strength.display}
                </span>
                <span className="text-muted-foreground">
                    Use 8+ chars, mix of case &amp; numbers
                </span>
            </div>
        </div>
    );
}

export default PasswordStrengthMeter;
